import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, ShieldCheck } from 'lucide-react';

/** Filterbediening van Data (Menu 4) van de professional: één rij boven de grafieken. */

export const FilterRij: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">{children}</div>
);

export function Keuze<T extends string | number>({
  label,
  waarde,
  opties,
  onChange,
}: {
  label: string;
  waarde: T;
  opties: { waarde: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <label className="text-xs font-medium text-gray-500">
      {label}
      <select
        value={String(waarde)}
        onChange={(e) => {
          const gekozen = opties.find((o) => String(o.waarde) === e.target.value);
          if (gekozen) onChange(gekozen.waarde);
        }}
        className="mt-1 block rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900"
      >
        {opties.map((o) => (
          <option key={String(o.waarde)} value={String(o.waarde)}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function Knoppenbalk<T extends string>({
  waarde,
  opties,
  onChange,
  label,
}: {
  waarde: T;
  opties: { waarde: T; label: React.ReactNode }[];
  onChange: (v: T) => void;
  label?: string;
}) {
  return (
    <div role="group" aria-label={label} className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
      {opties.map((o) => (
        <button
          key={o.waarde}
          type="button"
          aria-pressed={waarde === o.waarde}
          onClick={() => onChange(o.waarde)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${waarde === o.waarde ? 'bg-white text-navy-900 shadow-sm' : 'text-gray-600 hover:text-navy-900'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export const Fout: React.FC<{ fout: unknown }> = ({ fout }) => {
  const { t } = useTranslation('professionalData');
  const msg = (fout as any)?.response?.data?.message;
  return <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{msg || t('fout')}</div>;
};

export const Grendel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
    <Lock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden />
    <span>{children}</span>
  </p>
);

export const PrivacyNoot: React.FC = () => {
  const { t } = useTranslation('professionalData');
  return (
    <p className="flex items-start gap-2 text-xs text-gray-500">
      <ShieldCheck className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-navy-700" aria-hidden />
      <span>
        <strong className="font-semibold text-gray-700">{t('privacy.titel')}.</strong> {t('privacy.tekst')}
      </span>
    </p>
  );
};

export const datumNl = (iso: string, taal = 'nl') =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(taal.startsWith('en') ? 'en-GB' : 'nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
export const getal = (v: number, dec = 1) => v.toLocaleString('nl-NL', { minimumFractionDigits: dec, maximumFractionDigits: dec });
export const jaarOpties = (vanaf = 2024) => {
  const nu = new Date().getFullYear();
  return Array.from({ length: nu - vanaf + 1 }, (_, i) => nu - i).map((j) => ({ waarde: j, label: String(j) }));
};
